import {
	Alert,
	Box,
	Button,
	Checkbox,
	Container,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormGroup,
	FormLabel,
	LinearProgress,
	Paper,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { type FormikErrors, getIn, useFormik } from 'formik';
import { type ChangeEvent, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';

import Logo from '../../components/logo';
import type { AnswerInput, QuestionProps } from '../../models';
import {
	useGetQuestionnaireFillQuery,
	useSubmitQuestionnaireFillMutation,
} from '../../slices/apis/questionnaireFillApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toStringAnswer(answer: string | string[] | null | undefined): string {
	if (!answer) return '';
	return Array.isArray(answer) ? '' : answer;
}

function toArrayAnswer(answer: string | string[] | null | undefined): string[] {
	if (!answer) return [];
	return Array.isArray(answer) ? answer : [];
}

function isRequiredAnswerMissing(question: QuestionProps, answer: string | string[] | null | undefined): boolean {
	if (!question.isRequired || question.type === 'CONSENT') {
		return false;
	}

	if (question.type === 'MULTIPLE_CHOICE' && question.selectionType === 'MULTI') {
		return toArrayAnswer(answer).length === 0;
	}

	return toStringAnswer(answer).trim() === '';
}

type QuestionnaireFormValues = {
	answers: Record<string, string | string[]>;
	consentGiven: boolean;
};

function buildInitialValues(questions: QuestionProps[]): QuestionnaireFormValues {
	const answers: Record<string, string | string[]> = {};

	for (const question of questions) {
		if (question.type === 'CONSENT') {
			continue;
		}

		const questionId = String(question.id);

		if (question.answer != null) {
			answers[questionId] = Array.isArray(question.answer) ? question.answer : String(question.answer);
			continue;
		}

		answers[questionId] = question.type === 'MULTIPLE_CHOICE' && question.selectionType === 'MULTI' ? [] : '';
	}

	return {
		answers,
		consentGiven: false,
	};
}

// ── Question renderers ────────────────────────────────────────────────────────

function YesNoQuestion({
	question,
	name,
	value,
	onChange,
	onBlur,
	disabled,
	error,
}: {
	question: QuestionProps;
	name: string;
	value: string;
	onChange: (val: string) => void;
	onBlur: () => void;
	disabled: boolean;
	error?: string;
}) {
	return (
		<FormControl component="fieldset" required={question.isRequired} disabled={disabled} error={Boolean(error)}>
			<FormLabel
				component="legend"
				sx={{ mb: 0.5, fontWeight: 500, color: 'text.primary', '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
			>
				{question.text}
			</FormLabel>
			<RadioGroup row name={name} value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}>
				<FormControlLabel value="YES" control={<Radio size="small" />} label="Yes" />
				<FormControlLabel value="NO" control={<Radio size="small" />} label="No" />
			</RadioGroup>
			{error && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	);
}

function SingleChoiceQuestion({
	question,
	name,
	value,
	onChange,
	onBlur,
	disabled,
	error,
}: {
	question: QuestionProps;
	name: string;
	value: string;
	onChange: (val: string) => void;
	onBlur: () => void;
	disabled: boolean;
	error?: string;
}) {
	return (
		<FormControl component="fieldset" required={question.isRequired} disabled={disabled} error={Boolean(error)}>
			<FormLabel
				component="legend"
				sx={{ mb: 0.5, fontWeight: 500, color: 'text.primary', '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
			>
				{question.text}
			</FormLabel>
			<RadioGroup name={name} value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}>
				{(question.options ?? []).map((opt) => (
					<FormControlLabel key={opt.label} value={opt.label} control={<Radio size="small" />} label={opt.label} />
				))}
			</RadioGroup>
			{error && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	);
}

function MultiChoiceQuestion({
	question,
	name,
	value,
	onChange,
	onBlur,
	disabled,
	error,
}: {
	question: QuestionProps;
	name: string;
	value: string[];
	onChange: (val: string[]) => void;
	onBlur: () => void;
	disabled: boolean;
	error?: string;
}) {
	const handleChange = (label: string, checked: boolean) => {
		const opt = (question.options ?? []).find((o) => o.label === label);
		if (checked) {
			// If exclusive, deselect everything else
			if (opt?.exclusive) {
				onChange([label]);
			} else {
				// Remove any currently-selected exclusive options, then add
				const exclusiveLabels = (question.options ?? []).filter((o) => o.exclusive).map((o) => o.label);
				const next = [...value.filter((v) => !exclusiveLabels.includes(v)), label];
				onChange(next);
			}
		} else {
			onChange(value.filter((v) => v !== label));
		}
	};

	return (
		<FormControl component="fieldset" required={question.isRequired} disabled={disabled} error={Boolean(error)}>
			<FormLabel
				component="legend"
				sx={{ mb: 0.5, fontWeight: 500, color: 'text.primary', '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
			>
				{question.text}
			</FormLabel>
			<FormGroup>
				{(question.options ?? []).map((opt) => (
					<FormControlLabel
						key={opt.label}
						control={
							<Checkbox
								name={name}
								size="small"
								checked={value.includes(opt.label)}
								onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(opt.label, e.target.checked)}
								onBlur={onBlur}
							/>
						}
						label={opt.label}
					/>
				))}
			</FormGroup>
			{error && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	);
}

function TextQuestion({
	question,
	name,
	value,
	onChange,
	onBlur,
	disabled,
	error,
}: {
	question: QuestionProps;
	name: string;
	value: string;
	onChange: (val: string) => void;
	onBlur: () => void;
	disabled: boolean;
	error?: string;
}) {
	return (
		<TextField
			name={name}
			variant="standard"
			label={question.text}
			required={question.isRequired}
			disabled={disabled}
			multiline
			minRows={2}
			fullWidth
			value={value}
			onChange={(e) => onChange(e.target.value)}
			onBlur={onBlur}
			size="small"
			error={Boolean(error)}
			helperText={error}
		/>
	);
}

function ConsentQuestion({ question }: { question: QuestionProps }) {
	if (!question.consentContent) return null;
	return (
		<Box
			sx={{
				fontSize: '0.9rem',
				lineHeight: 1.7,
				color: 'text.primary',
				'& ol, & ul': { pl: 3, mb: 1.5 },
				'& li': { mb: 1 },
				'& p': { mb: 1 },
				'& em': { fontStyle: 'italic' },
				'& strong, & b': { fontWeight: 600 },
			}}
			// biome-ignore lint/security/noDangerouslySetInnerHtml: consent HTML is trusted server-authored content
			dangerouslySetInnerHTML={{ __html: question.consentContent }}
		/>
	);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function QuestionnaireFill() {
	const navigate = useNavigate();
	const { token } = useParams() as { token: string };

	const { data, isLoading, isError, error } = useGetQuestionnaireFillQuery(token);
	const [submitFill, { isLoading: isSubmitting }] = useSubmitQuestionnaireFillMutation();

	const [submitError, setSubmitError] = useState<string | null>(null);

	const questions = useMemo(() => data?.sections.flatMap((section) => section.questions) ?? [], [data]);
	const initialValues = useMemo(() => buildInitialValues(questions), [questions]);

	const disabled = isSubmitting;

	const formik = useFormik<QuestionnaireFormValues>({
		initialValues,
		enableReinitialize: true,
		validate: (values) => {
			const errors: FormikErrors<QuestionnaireFormValues> = {};
			const answerErrors: Record<string, string> = {};

			for (const question of questions) {
				if (isRequiredAnswerMissing(question, values.answers[String(question.id)])) {
					answerErrors[String(question.id)] = 'This question is required.';
				}
			}

			if (Object.keys(answerErrors).length > 0) {
				errors.answers = answerErrors;
			}

			if (!values.consentGiven) {
				errors.consentGiven =
					'Please confirm that the information provided is true and that you consent to processing.';
			}

			return errors;
		},
		onSubmit: async (values, { resetForm }) => {
			setSubmitError(null);
			const answerInputs: AnswerInput[] = questions
				.filter((question) => question.type !== 'CONSENT')
				.map((question) => ({
					questionId: question.id,
					answer: values.answers[String(question.id)],
				}));

			try {
				await submitFill({ token, body: { answers: answerInputs } }).unwrap();
				resetForm({ values: { ...values, consentGiven: false } });
				navigate(`/questionnaire/fill/${token}/submitted`, {
					state: { justSubmitted: true },
				});
			} catch (err: unknown) {
				const e = err as { data?: { error?: string } | string; status?: number };
				if (e.status === 410) {
					setSubmitError('This questionnaire link has expired. Please contact AusRehab to request a new link.');
				} else {
					const raw = e.data;
					const msg =
						typeof raw === 'string'
							? raw
							: typeof raw === 'object' && raw !== null && typeof raw.error === 'string'
								? raw.error
								: 'Submission failed. Please check your answers and try again.';
					setSubmitError(msg);
				}
			}
		},
	});

	if (isLoading) {
		return (
			<Container maxWidth="md" sx={{ mt: 8 }}>
				<LinearProgress />
				<Typography align="center" sx={{ mt: 2 }}>
					Loading questionnaire...
				</Typography>
			</Container>
		);
	}

	if (isError) {
		const e = error as { status?: number };
		const expired = e.status === 410;
		return (
			<Container maxWidth="md" sx={{ mt: 8 }}>
				<Alert severity="error">
					{expired
						? 'This questionnaire link has expired. Please contact AusRehab to request a new link.'
						: 'Unable to load questionnaire. The link may be invalid.'}
				</Alert>
			</Container>
		);
	}

	return (
		<>
			<Helmet>
				<title>Pre-Employment Questionnaire — {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
				<form onSubmit={formik.handleSubmit}>
					<Stack spacing={4} sx={{ pb: 1 }}>
						<Box sx={{ display: 'flex', justifyContent: 'center', '& img': { width: 216, height: 'auto' } }}>
							<Logo />
						</Box>

						<Box>
							<Typography variant="h4" gutterBottom>
								Pre-Employment Questionnaire
							</Typography>
							{data?.prePopulated && (
								<Alert severity="info" sx={{ mb: 2 }}>
									Your previous answers have been pre-filled. Please review and update if anything has changed.
								</Alert>
							)}
						</Box>

						{data?.sections.map((section) => (
							<Paper key={section.id} variant="outlined" sx={{ p: 3 }}>
								<Typography variant="h6" gutterBottom>
									{section.name}
								</Typography>
								{section.description && (
									<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
										{section.description}
									</Typography>
								)}
								<Divider sx={{ mb: 2 }} />
								<Stack spacing={3}>
									{section.questions.map((q) => {
										const answerFieldName = `answers.${q.id}`;
										const answerError =
											getIn(formik.touched, answerFieldName) || formik.submitCount > 0
												? getIn(formik.errors, answerFieldName)
												: undefined;

										if (q.type === 'CONSENT') {
											return <ConsentQuestion key={q.id} question={q} />;
										}
										if (q.type === 'YES_NO') {
											return (
												<YesNoQuestion
													key={q.id}
													question={q}
													name={answerFieldName}
													value={toStringAnswer(formik.values.answers[String(q.id)])}
													onChange={(v) => formik.setFieldValue(answerFieldName, v)}
													onBlur={() => formik.setFieldTouched(answerFieldName, true)}
													disabled={disabled}
													error={answerError}
												/>
											);
										}
										if (q.type === 'MULTIPLE_CHOICE' && q.selectionType === 'MULTI') {
											return (
												<MultiChoiceQuestion
													key={q.id}
													question={q}
													name={answerFieldName}
													value={toArrayAnswer(formik.values.answers[String(q.id)])}
													onChange={(v) => formik.setFieldValue(answerFieldName, v)}
													onBlur={() => formik.setFieldTouched(answerFieldName, true)}
													disabled={disabled}
													error={answerError}
												/>
											);
										}
										if (q.type === 'MULTIPLE_CHOICE' && q.selectionType === 'SINGLE') {
											return (
												<SingleChoiceQuestion
													key={q.id}
													question={q}
													name={answerFieldName}
													value={toStringAnswer(formik.values.answers[String(q.id)])}
													onChange={(v) => formik.setFieldValue(answerFieldName, v)}
													onBlur={() => formik.setFieldTouched(answerFieldName, true)}
													disabled={disabled}
													error={answerError}
												/>
											);
										}
										if (q.type === 'TEXT') {
											return (
												<TextQuestion
													key={q.id}
													question={q}
													name={answerFieldName}
													value={toStringAnswer(formik.values.answers[String(q.id)])}
													onChange={(v) => formik.setFieldValue(answerFieldName, v)}
													onBlur={() => formik.setFieldTouched(answerFieldName, true)}
													disabled={disabled}
													error={answerError}
												/>
											);
										}
										return null;
									})}
								</Stack>
							</Paper>
						))}

						{submitError && <Alert severity="error">{submitError}</Alert>}

						<Paper variant="outlined" sx={{ p: 3 }}>
							<FormControl error={Boolean(formik.touched.consentGiven && formik.errors.consentGiven)}>
								<FormControlLabel
									control={
										<Checkbox
											name="consentGiven"
											checked={formik.values.consentGiven}
											onChange={(e) => formik.setFieldValue('consentGiven', e.target.checked)}
											onBlur={formik.handleBlur}
											disabled={disabled}
										/>
									}
									label={
										<Typography variant="body2">
											I acknowledge that the information provided is true and accurate, and I consent to AusRehab
											processing my health information for pre-employment screening purposes.
										</Typography>
									}
								/>
								{formik.touched.consentGiven && formik.errors.consentGiven && (
									<FormHelperText>{formik.errors.consentGiven}</FormHelperText>
								)}
							</FormControl>
						</Paper>

						<Button loading={isSubmitting} variant="contained" size="large" disabled={disabled} type="submit">
							{isSubmitting ? 'Submitting...' : 'Submit Questionnaire'}
						</Button>
					</Stack>
				</form>
			</Container>
		</>
	);
}
